"use client";

import type {
  LeadFormFieldChangeHandler,
  MultiSelectOption,
} from "./form-types";

import { useCallback, useState } from "react";
import { useLeadFormPersistedValues } from "./LeadFormPersistedValuesContext";

/** Resolves an option's identity for both the DOM value and the persisted key. */
function resolveOptionValue(option: MultiSelectOption): string {
  return option.value ?? option.label ?? option._key;
}

/**
 * Rebuilds the initially-selected option set from persisted machine values.
 * Walks `options` (not all persisted keys) so a field name that prefixes
 * another field's name cannot collide on a "${fieldName}.${value}" lookup.
 */
function buildSelectedSet(
  fieldName: string | null,
  options: MultiSelectOption[],
  persistedFieldValues: Record<string, string>,
): Set<string> {
  if (!fieldName) {
    return new Set();
  }

  const selected = new Set<string>();
  for (const option of options) {
    const optionValue = resolveOptionValue(option);
    if (persistedFieldValues[`${fieldName}.${optionValue}`] === "on") {
      selected.add(optionValue);
    }
  }
  return selected;
}

/**
 * Seeds a multi-select field's selected set from the machine's persisted
 * values on mount — the mechanism that survives the step region's
 * unmount/remount when it moves into a fullscreen takeover Dialog — and
 * reports every toggle back to the machine
 * through `onFieldChange` as "${fieldName}.${value}" -> "on" / "".
 */
function usePersistedMultiSelectState(
  fieldName: string | null,
  options: MultiSelectOption[],
  onFieldChange?: LeadFormFieldChangeHandler,
): [Set<string>, (value: string, checked: boolean) => void] {
  const { persistedFieldValues } = useLeadFormPersistedValues();
  const [selected, setSelected] = useState<Set<string>>(() =>
    buildSelectedSet(fieldName, options, persistedFieldValues),
  );

  const setOptionSelected = useCallback(
    (value: string, checked: boolean) => {
      setSelected((prev) => {
        const next = new Set(prev);
        if (checked) {
          next.add(value);
        } else {
          next.delete(value);
        }
        return next;
      });
      if (fieldName) {
        onFieldChange?.(`${fieldName}.${value}`, checked ? "on" : "");
      }
    },
    [fieldName, onFieldChange],
  );

  return [selected, setOptionSelected];
}

export { resolveOptionValue, usePersistedMultiSelectState };
